import { useEffect, useMemo, useRef } from "react";

// Interfaces
import { IDealBrand, IMiniDeal, IMiniDealExtension } from "./interfaces";

// Default sections
import { MiniDealBrand } from "@/PayPlans/helpers/DefaultSections";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";
import { useAvailableBrands } from "./helpers";

// Hooks
import { useCallbackState } from "@/Common/hooks/CallbackState";
import { useReferenceState } from "@/Common/hooks/ReferenceState";
import { useValidations } from "@/Common/hooks/Validations";

// Layouts
import TileSelectButton from "@/Layouts/components/Buttons/TileSelectButton";

// Custom components
import MiniDealModel from "./MiniDealModel";

// MUI
import { getRelativeYear } from "@/Common/utils/DateUtils";
import { hasOverlap } from "@/Common/utils/ValidationUtils";
import { Grid, Grow } from "@mui/material";

export default function MiniDealExtension(props: Readonly<IMiniDealExtension>) {
	const {
		identifier = "",
		miniDeals = [],
		brands = [],
		isSpecificModelsAllowed = false,
		loading = false,
		readOnly = false,
		onActionUpdate = () => {},
		onActionValidation = () => {},
	} = props;

	// Hooks
	const { errors, isValid, validate } = useValidations();
	const dealsErrors = errors?.get("miniDeals");

	// Control to send update actions
	const wasUpdated = useRef(false);

	const onMiniDealsUpdate = (updatedDeals: IMiniDeal[]) => {
		const valid = Boolean(isValid && isValidModel);
		onActionValidation?.(valid);
		if (valid && wasUpdated.current) {
			wasUpdated.current = false;
			const updated = [...updatedDeals];
			onActionUpdate?.(updated, valid);
		}
	};

	// States
	const [innerMiniDeals, setMiniDeals] = useCallbackState([], onMiniDealsUpdate);
	const [isValidMiniDeals, setIsValidMiniDeals, validMiniDealsList, recheckValidMiniDeals] = useReferenceState(true, []);
	const isValidModel = useMemo(() => isValidMiniDeals, [isValidMiniDeals]);
	const availableBrands = useAvailableBrands(brands, innerMiniDeals.addons);

	const updateMiniDeals = (deals: IMiniDeal[]) => {
		wasUpdated.current = true;
		setMiniDeals(deals);
	};

	const handleUpdateDeal = (deal: IMiniDeal, index: number) => {
		const updatedMiniDeals = [...innerMiniDeals];
		updatedMiniDeals[index] = { ...deal };
		updateMiniDeals(updatedMiniDeals);
	};

	const handleAddMiniDeal = function (deal: IDealBrand) {
		const updatedMiniDeals = [...innerMiniDeals];
		updatedMiniDeals.push(MiniDealBrand(deal.make));
		updateMiniDeals(updatedMiniDeals);
	};

	const handleDeleteDeal = (index: number) => {
		const updatedMiniDeals = [...innerMiniDeals];
		if (index !== -1) {
			updatedMiniDeals.splice(index, 1);
			validMiniDealsList[index] = true;
			recheckValidMiniDeals();
		}
		updateMiniDeals(updatedMiniDeals);
	};

	useMemo(() => {
		if (innerMiniDeals?.length) {
			validate([
				{
					definition: {
						useKey: "miniDeals",
						operators: {
							conditions: [
								{
									type: "HIERARCHICAL_UNIQUE",
									hierarchy: ["make", "model", "modelYears"],
								},
								{
									type: "INTERSECTION",
									on: [
										{
											key: "modelYears.left",
											useModifier: {
												function: "getRelativeYear",
												when: { key: "modelYears.rangeType", value: "RELATIVE" },
											},
										},
										{
											key: "modelYears.right",
											useModifier: {
												function: "getRelativeYear",
												when: { key: "modelYears.rangeType", value: "RELATIVE" },
											},
										},
									],
									useModifier: { function: "hasOverlap" },
									logic: "not",
									applyIf: {
										type: "HIERARCHICAL_UNIQUE",
										hierarchy: ["make", "model"],
										logic: "not",
									},
								},
							],
							modifiers: {
								hasOverlap,
								getRelativeYear,
							},
							maxInvalidConditions: 2,
						},
						errorsMap: ["make", "model", "modelYears"],
						message: "Invalid model definition",
						validationType: "COMPARATIVE",
					},
					section: innerMiniDeals || [],
				},
			]);
		}
	}, [identifier, innerMiniDeals, validate]);

	useEffect(() => {
		setMiniDeals(clone(miniDeals));
		return () => {
			setIsValidMiniDeals(true);
			setMiniDeals([]);
			wasUpdated.current = false;
		};
	}, [miniDeals, setMiniDeals, setIsValidMiniDeals]);

	return (
		<Grid container spacing={2} sx={{ maxWidth: "1160px", marginTop: "20px" }}>
			{innerMiniDeals?.map((deal: IMiniDeal, key: number) => {
				const itemKey = `mini_deal_default_${key}`;
				return (
					<Grow in timeout={500} key={itemKey}>
						<Grid item>
							<MiniDealModel
								index={key}
								data={deal}
								deletable
								modelSelectable={true}
								error={dealsErrors?.[key]?.model ?? false}
								allowSpecificModels={isSpecificModelsAllowed}
								readOnly={readOnly}
								onActionDelete={handleDeleteDeal}
								onActionUpdate={(miniDeal) => handleUpdateDeal(miniDeal, key)}
								onActionValidation={(value: boolean) => {
									validMiniDealsList[key] = value;
									recheckValidMiniDeals();
								}}
							/>
						</Grid>
					</Grow>
				);
			})}

			<Grid item>
				<TileSelectButton
					tooltip={"Add Model"}
					data={availableBrands}
					sortBy={"make"}
					showBy={"make"}
					onSelect={handleAddMiniDeal}
					disabled={loading || readOnly || !(isValid && isValidModel)}
					loading={loading}
				/>
			</Grid>
		</Grid>
	);
}
